import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { DisplayL } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_PRO_FIXTURE_ENTER_SEASONALITY_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LastOwnedCardsBySo5Competition } from '@sorare/marketplace/src/components/player/LastOwnedCardsBySo5Competition';
import { Header as ScoutingCenterHeaderGeneric } from '@sorare/marketplace/src/searchCommonPlayer/Header';

import { CompetitionButton } from './CompetitionButton';
import {
  ScoutingCenterHeader_competition,
  ScoutingCenterHeader_so5Competition,
  ScoutingCenterHeader_so5LeaderboardGroup,
} from './__generated__/index.graphql';
import proIcon from './assets/pro.svg';

type Props = {
  so5Competition: ScoutingCenterHeader_so5Competition;
  competitions?: ScoutingCenterHeader_competition[];
  so5LeaderboardGroupInterface?: ScoutingCenterHeader_so5LeaderboardGroup;
};

const So5LeaderboardGroupLogo = styled.img`
  height: calc(6 * var(--unit));
  @media ${laptopAndAbove} {
    height: calc(12 * var(--unit));
  }
  width: auto;
`;

const Row = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-start;
  flex-grow: 1;
`;

const Leagues = styled(Horizontal)`
  flex-wrap: wrap;
  width: 100%;
`;

const Title = styled(DisplayL)`
  @media ${laptopAndAbove} {
    font-size: calc(12 * var(--unit));
    line-height: 1.1;
  }
`;

const LIMIT = 5;

export const ScoutingCenterHeader = ({
  so5Competition,
  competitions,
  so5LeaderboardGroupInterface,
}: Props) => {
  const { up: isLaptopOrAbove } = useScreenSize('laptop');

  const {
    displayName: so5CompetitionDisplayName,
    headerBackgroundUrl: so5CompetitionHeaderBackgroundUrl,
    headerBackgroundDesktopUrl: so5CompetitionHeaderBackgroundDesktopUrl,
    iconUrl,
    scoutable,
    playableSo5LeaderboardGroupInterfaces,
  } = so5Competition;

  const {
    headerBackgroundUrl: so5LeaderboardGroupHeaderBackgroundUrl,
    displayName: so5LeaderboardGroupDisplayName,
  } = so5LeaderboardGroupInterface || {};

  const { headerBackgroundUrl, headerBackgroundDesktopUrl, displayName } =
    (() => {
      if (!scoutable && so5LeaderboardGroupInterface)
        return {
          headerBackgroundUrl: so5LeaderboardGroupHeaderBackgroundUrl,
          headerBackgroundDesktopUrl: so5LeaderboardGroupHeaderBackgroundUrl,
          displayName: so5LeaderboardGroupDisplayName,
        };
      return {
        headerBackgroundUrl: so5CompetitionHeaderBackgroundUrl,
        headerBackgroundDesktopUrl: so5CompetitionHeaderBackgroundDesktopUrl,
        displayName: so5CompetitionDisplayName,
      };
    })();

  const limit = isLaptopOrAbove ? LIMIT : 0;

  const hasDomesticLeagues = (competitions?.length || 0) > 1;
  const hasMoreDomesticLeagues = (competitions?.length || 0) > limit;

  const displayableLeagues = competitions?.slice(0, limit);
  const hiddenLeagues = competitions?.slice(limit);

  const backgroundUrl =
    (isLaptopOrAbove && headerBackgroundDesktopUrl) || headerBackgroundUrl;

  const nextPlayableSo5LeaderboardGroupInterface =
    so5LeaderboardGroupInterface || playableSo5LeaderboardGroupInterfaces?.[0];

  return (
    <ScoutingCenterHeaderGeneric backgroundImage={backgroundUrl!}>
      <Row>
        <LastOwnedCardsBySo5Competition so5Competition={so5Competition} />
        {!!nextPlayableSo5LeaderboardGroupInterface && (
          <Button
            size="compact"
            color="quaternary"
            blur
            startIcon={<img src={proIcon} alt="" />}
            to={generatePath(
              FOOTBALL_PLAY_PRO_FIXTURE_ENTER_SEASONALITY_COMPETITION,
              {
                fixture:
                  nextPlayableSo5LeaderboardGroupInterface.so5Fixture.slug,
                seasonality:
                  nextPlayableSo5LeaderboardGroupInterface.seasonalities.includes(
                    So5LeaderboardSeasonality.IN_SEASON
                  )
                    ? So5LeaderboardSeasonality.IN_SEASON
                    : So5LeaderboardSeasonality.ALL_SEASONS,
                competition: nextPlayableSo5LeaderboardGroupInterface.slug,
              }
            )}
          >
            <FormattedMessage {...fantasy.setLineup} />
          </Button>
        )}
      </Row>
      <So5LeaderboardGroupLogo src={iconUrl} alt={`${displayName}-logo`} />
      <Title>{displayName}</Title>
      {hasDomesticLeagues && (
        <Leagues>
          {displayableLeagues?.map(league => (
            <CompetitionButton key={league.slug} competition={league} />
          ))}
          {hasMoreDomesticLeagues && (
            <Tooltip
              title={
                <Leagues>
                  {hiddenLeagues?.map(league => (
                    <CompetitionButton key={league.slug} competition={league} />
                  ))}
                </Leagues>
              }
            >
              <Button
                color="quaternary"
                size="compact"
                endIcon={<ChevronDownBold />}
              >
                {isLaptopOrAbove ? (
                  `+${hiddenLeagues?.length}`
                ) : (
                  <FormattedMessage
                    id="scoutingCenterHeader.countLeagues"
                    defaultMessage="{count, plural, one {1 league} other {# leagues}}"
                    values={{ count: hiddenLeagues?.length }}
                  />
                )}
              </Button>
            </Tooltip>
          )}
        </Leagues>
      )}
    </ScoutingCenterHeaderGeneric>
  );
};

const so5LeaderboardGroupFragment = gql`
  fragment ScoutingCenterHeader_so5LeaderboardGroup on So5LeaderboardGroupInterface {
    slug
    displayName
    seasonalities
    iconUrl
    so5Fixture {
      slug
    }
    headerBackgroundUrl(seasonality: IN_SEASON)
  }
` as TypedDocumentNode<ScoutingCenterHeader_so5LeaderboardGroup>;

ScoutingCenterHeader.fragments = {
  so5Competition: gql`
    fragment ScoutingCenterHeader_so5Competition on So5Competition {
      slug
      displayName
      headerBackgroundUrl
      headerBackgroundDesktopUrl
      iconUrl
      scoutable
      playableSo5LeaderboardGroupInterfaces {
        slug
        ...ScoutingCenterHeader_so5LeaderboardGroup
      }
      ...LastOwnedCardsBySo5Competition_so5Competition
    }
    ${so5LeaderboardGroupFragment}
    ${LastOwnedCardsBySo5Competition.fragments.so5Competition}
  ` as TypedDocumentNode<ScoutingCenterHeader_so5Competition>,
  competition: gql`
    fragment ScoutingCenterHeader_competition on Competition {
      slug
      ...ScoutingCenterHeaderCompetitionButton_competition
    }
    ${CompetitionButton.fragments.competition}
  ` as TypedDocumentNode<ScoutingCenterHeader_competition>,
  so5LeaderboardGroup: so5LeaderboardGroupFragment,
};
